"use client";

/**
 * Prefer-over discrete ordinal scale (Effort Faster←→Smarter).
 * D19 — DialKitSlider Prefer-over for the track (replaces custom motion thumb).
 */

import { InfoIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useMemo } from "react";
import { cn } from "../../lib";
import {
	LANDING_BEAM_FROM,
	LANDING_BEAM_TO,
} from "../../lib/design-system/landing-beam-colors";
import { DialKitSlider } from "../design-system/dialkit/dialkit-slider";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface DiscreteScaleOption {
	value: string;
	label: string;
}

export interface DiscreteScaleFieldProps {
	id?: string;
	/** FieldShell label when not omitting shell; otherwise unused. */
	label?: string;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	/** Ordered discrete steps (index 0 = left / Faster). */
	options: DiscreteScaleOption[];
	value: string;
	onChange: (value: string) => void;
	/** Left axis caption (e.g. Faster). */
	lowAxisLabel: string;
	/** Right axis caption (e.g. Smarter). */
	highAxisLabel: string;
	/**
	 * Expanded header title prefix (e.g. "Effort"). Combined with the selected
	 * option label as `{title} {valueLabel}`.
	 */
	title?: string;
	/** Explicit value label override; defaults to selected option label. */
	valueLabel?: string;
	helpText?: ReactNode;
	helpAriaLabel?: string;
	maxGradientFrom?: string;
	maxGradientTo?: string;
	/**
	 * When true, filled range uses the max beam gradient. Defaults to true when
	 * `value` is the last option.
	 */
	applyMaxGradient?: boolean;
	/** Skip FieldShell (popover / picker panel). */
	omitShell?: boolean;
	testId?: string;
}

export function DiscreteScaleField({
	id,
	label,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	disabled = false,
	layoutHint,
	className,
	options,
	value,
	onChange,
	lowAxisLabel,
	highAxisLabel,
	title,
	valueLabel,
	helpText,
	helpAriaLabel,
	maxGradientFrom = LANDING_BEAM_FROM,
	maxGradientTo = LANDING_BEAM_TO,
	applyMaxGradient,
	omitShell = false,
	testId = "discrete-scale-field",
}: DiscreteScaleFieldProps) {
	const committedIndex = useMemo(() => {
		const found = options.findIndex((option) => option.value === value);
		return found >= 0 ? found : Math.max(0, Math.floor(options.length / 2));
	}, [options, value]);
	const lastIndex = Math.max(0, options.length - 1);

	const selectedLabel =
		valueLabel ?? options[committedIndex]?.label ?? options[0]?.label ?? "";
	const useMaxGradient =
		applyMaxGradient ??
		(options.length > 0 && committedIndex === lastIndex);
	const headerText =
		title != null && title.length > 0
			? `${title} ${selectedLabel}`
			: selectedLabel;

	function commitIndex(nextIndex: number) {
		const snapped = Math.max(0, Math.min(lastIndex, Math.round(nextIndex)));
		const next = options[snapped];
		if (next && next.value !== value) {
			onChange(next.value);
		}
	}

	const control = (
		<div
			className={cn("flex w-full flex-col gap-3", omitShell && className)}
			data-field-type="discrete_scale"
			data-prefer-over="dialkit-slider"
			data-testid={testId}
			data-value={value}
			data-max-gradient={useMaxGradient ? "true" : "false"}
			style={
				{
					"--effort-beam-from": maxGradientFrom,
					"--effort-beam-to": maxGradientTo,
				} as CSSProperties
			}
			onBlur={onBlur}
		>
			{name ? <input type="hidden" name={name} value={value} /> : null}
			{(title != null && title.length > 0) || helpText != null ? (
				<div className="flex min-w-0 items-center justify-between gap-2">
					<p
						className="min-w-0 truncate text-sm font-medium text-foreground"
						data-testid={`${testId}-header`}
					>
						{headerText}
					</p>
					{helpText != null ? (
						<ContentTooltip
							{...CONTENT_TOOLTIP_SHORT}
							side="bottom"
							contentClassName="max-w-xs text-xs leading-snug"
							content={helpText}
						>
							<span
								role="button"
								tabIndex={0}
								className="inline-flex size-6 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground hover:bg-muted/60 hover:text-foreground"
								aria-label={
									helpAriaLabel ??
									(typeof helpText === "string"
										? helpText
										: "Help")
								}
								data-testid={`${testId}-help`}
							>
								<InfoIcon className="size-3.5" aria-hidden />
							</span>
						</ContentTooltip>
					) : null}
				</div>
			) : null}

			<div className="flex items-center justify-between gap-2 text-[11px] font-medium leading-none text-muted-foreground">
				<span data-testid={`${testId}-axis-low`}>{lowAxisLabel}</span>
				<span data-testid={`${testId}-axis-high`}>{highAxisLabel}</span>
			</div>

			<div
				className={cn(
					"relative w-full rounded-full p-0.5",
					useMaxGradient &&
						"bg-[linear-gradient(90deg,var(--effort-beam-from),var(--effort-beam-to))] p-[2px]",
				)}
				data-testid={`${testId}-track-shell`}
			>
				<div className="pointer-events-none absolute inset-x-3 inset-y-0 z-[1] flex items-stretch justify-between">
					{options.map((option, optionIndex) => {
						const isActive = optionIndex === committedIndex;
						return (
							<button
								key={option.value}
								type="button"
								tabIndex={-1}
								disabled={disabled}
								className="pointer-events-auto flex min-h-11 min-w-11 flex-1 cursor-pointer items-stretch justify-center border-0 bg-transparent p-0"
								aria-label={option.label}
								aria-current={isActive ? "true" : undefined}
								data-testid={`${testId}-tick-${option.value}`}
								data-discrete-tick-active={
									isActive ? "true" : "false"
								}
								onClick={() => {
									if (disabled) {
										return;
									}
									commitIndex(optionIndex);
								}}
							>
								<span
									className={cn(
										"mx-auto block self-stretch transition-[width,background-color,transform] duration-150",
										isActive
											? "w-1 scale-y-110 bg-foreground shadow-sm dark:bg-white"
											: "w-px bg-white/60 dark:bg-white/35",
									)}
									aria-hidden
								/>
							</button>
						);
					})}
				</div>

				<div
					data-testid={`${testId}-slider`}
					className={cn(
						"relative z-[2] rounded-full bg-background/90 px-1 py-0.5",
						disabled && "opacity-50",
					)}
				>
					{/* D19 — DialKit Prefer-over discrete index slider. */}
					<DialKitSlider
						label={label ?? title ?? selectedLabel}
						hideLabel
						min={0}
						max={lastIndex}
						step={1}
						value={committedIndex}
						disabled={disabled}
						onChange={(next) => commitIndex(next)}
					/>
					{/* Grip affordance marker for contracts / a11y chrome. */}
					<span
						className="sr-only"
						data-testid={`${testId}-thumb-grip`}
					>
						{selectedLabel}
					</span>
				</div>
			</div>
		</div>
	);

	if (omitShell) {
		return control;
	}

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={className}
		>
			{control}
		</FieldShell>
	);
}
